import { useState, useEffect } from 'react';
import {
  ArrowLeft, Phone, Mail, MapPin, Clock, Users, Building, ShieldCheck,
  MessageSquare, ExternalLink, Navigation, Compass, Car, Footprints, Bus,
  UserCheck, Sparkles, Send, GraduationCap, User, CheckCircle2, School
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '../../components/ui/UserAvatar';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';
import { ResponsavelTabBar } from '../../components/responsavel/ResponsavelTabBar';

interface ProfessorVinculado {
  id: string;
  nome: string;
  cargo?: string;
  disciplina?: string;
  email?: string;
  telefone?: string;
  fotoUrl?: string;
}

interface FilhoEscola {
  id: string;
  nome: string;
  matricula?: string;
  fotoUrl?: string;
  turma?: string;
  turno?: string;
  professores: ProfessorVinculado[];
}

interface GestaoMembro {
  id: string;
  nome: string;
  cargo: string;
  email?: string;
  telefone?: string;
  fotoUrl?: string;
}

interface EscolaData {
  escola: {
    id: string;
    nome: string;
    codigo?: string;
    endereco?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
    telefone?: string;
    email?: string;
    nomeDiretor?: string;
    latitude?: number;
    longitude?: number;
  };
  direcao: GestaoMembro[];
  filhos: FilhoEscola[];
}

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function ResponsavelEscola() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const responsavelId = useResponsavelId();

  const [escolas, setEscolas] = useState<EscolaData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!responsavelId) return;
    
    setLoading(true);
    api.get(ROUTES.mobileResponsavel.escola(responsavelId))
      .then((res) => {
        const rawData = Array.isArray(res.data) ? res.data : [];
        const normalized: EscolaData[] = rawData.map((item: any, idx: number) => {
          const rawEscola = item.escola || {};
          const rawDirecao = item.direcao || item.equipe || [];
          const rawFilhos = item.filhos || item.filhosNaEscola || [];

          const mappedDirecao: GestaoMembro[] = Array.isArray(rawDirecao)
            ? rawDirecao.map((m: any, mIdx: number) => ({
                id: m.id || `gestao-${mIdx}`,
                nome: m.nome || 'Membro da Gestão',
                cargo: m.cargo || 'GESTAO_ESCOLAR',
                email: m.email || undefined,
                telefone: m.telefone || undefined,
                fotoUrl: m.fotoUrl || undefined,
              }))
            : [];

          const mappedFilhos: FilhoEscola[] = Array.isArray(rawFilhos)
            ? rawFilhos.map((f: any, fIdx: number) => ({
                id: f.id || `filho-${fIdx}`,
                nome: f.nome || 'Dependente',
                matricula: f.matricula || undefined,
                fotoUrl: f.fotoUrl || undefined,
                turma: f.turma || undefined,
                turno: f.turno || undefined,
                professores: Array.isArray(f.professores)
                  ? f.professores.map((p: any, pIdx: number) => ({
                      id: p.id || `prof-${pIdx}`,
                      nome: p.nome || 'Professor(a)',
                      cargo: p.cargo || 'PROFESSOR',
                      disciplina: p.disciplina || 'Regente',
                      email: p.email || undefined,
                      telefone: p.telefone || undefined,
                      fotoUrl: p.fotoUrl || undefined,
                    }))
                  : [],
              }))
            : [];

          return {
            escola: {
              id: rawEscola.id || `esc-${idx}`,
              nome: rawEscola.nome || 'Escola Vinculada',
              codigo: rawEscola.codigo || undefined,
              endereco: rawEscola.endereco || undefined,
              cidade: rawEscola.cidade || undefined,
              uf: rawEscola.uf || undefined,
              cep: rawEscola.cep || undefined,
              telefone: rawEscola.telefone || undefined,
              email: rawEscola.email || undefined,
              nomeDiretor: rawEscola.nomeDiretor || undefined,
              latitude: rawEscola.latitude || undefined,
              longitude: rawEscola.longitude || undefined,
            },
            direcao: mappedDirecao,
            filhos: mappedFilhos,
          };
        });

        setEscolas(normalized);
        setSelectedIndex(0);
      })
      .catch((err) => {
        console.error('Erro ao buscar dados reais da escola:', err);
        setEscolas([]);
      })
      .finally(() => setLoading(false));
  }, [responsavelId]);

  const selectedEscola = escolas[selectedIndex] || escolas[0];

  const formatCargo = (cargo: string) => {
    const map: Record<string, string> = {
      DIRETOR: 'Diretoria Geral',
      COORDENACAO: 'Coordenação Pedagógica',
      SECRETARIA: 'Secretaria e Matrículas',
      PROFESSOR: 'Professor(a) Regente',
      GESTAO_ESCOLAR: 'Equipe Gestora',
    };
    return map[cargo] || cargo;
  };

  const getInitials = (nome: string) => {
    return nome.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  };

  const handleOpenChat = (membro: { id?: string; nome: string; cargo?: string }, alunoContext?: string) => {
    const prefill = alunoContext
      ? `Olá ${membro.nome}, sou responsável do aluno(a) ${alunoContext} e gostaria de um esclarecimento.`
      : `Olá ${membro.nome}, sou responsável de aluno na escola e gostaria de um esclarecimento.`;

    window.dispatchEvent(
      new CustomEvent('ac:chat:open', {
        detail: {
          recipientId: membro.id,
          contatoId: membro.id,
          recipientName: membro.nome,
          recipientRole: membro.cargo,
          prefill,
        },
      })
    );
  };

  const getMapsUrl = (escola: EscolaData['escola']) => {
    const query = encodeURIComponent(`${escola.nome}, ${escola.endereco || ''}, ${escola.cidade || ''} - ${escola.uf || 'MG'}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  const getWazeUrl = (escola: EscolaData['escola']) => {
    const query = encodeURIComponent(`${escola.nome}, ${escola.endereco || ''}`);
    return `https://waze.com/ul?q=${query}`;
  };

  const getWhatsAppClean = (tel?: string) => {
    if (!tel) return null;
    const clean = tel.replace(/\D/g, '');
    if (clean.length < 8) return null;
    return clean.startsWith('55') ? clean : `55${clean}`;
  };

  // ═══ DESKTOP VIEW ═══
  if (!isMobile) {
    const totalProfessores = (selectedEscola?.filhos || []).reduce((acc, f) => acc + (f.professores?.length || 0), 0);

    return (
      <div className="admin-page" style={{ maxWidth: 1400, width: '100%', margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Full-Width Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <Building size={12} color="#818cf8" /> Unidade Escolar & Canais Oficiais
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Minha Escola & Contatos
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Direção, canais de atendimento da secretaria e professores vinculados às turmas dos seus filhos
            </p>
          </div>

          {/* Hero KPIs Reais */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>{escolas.length}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Escolas</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#4ade80', lineHeight: 1.1 }}>{selectedEscola?.filhos?.length || 0}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Alunos</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#fbbf24', lineHeight: 1.1 }}>{selectedEscola?.direcao?.length || 0}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Gestão</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#a78bfa', lineHeight: 1.1 }}>{totalProfessores}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Professores</div>
            </div>
          </div>
        </div>

        {/* ═══ Seletor de Escolas (quando há múltiplos filhos em escolas diferentes) ═══ */}
        {escolas.length > 1 && (
          <div style={{
            display: 'flex', gap: 10, marginBottom: 20, background: '#ffffff',
            padding: '12px 16px', borderRadius: 16, border: '1px solid #e2e8f0',
          }}>
            {escolas.map((escItem, idx) => {
              const active = idx === selectedIndex;
              return (
                <button
                  key={escItem.escola.id}
                  onClick={() => setSelectedIndex(idx)}
                  style={{
                    padding: '8px 16px', borderRadius: 10, border: '1px solid',
                    borderColor: active ? '#2563eb' : '#e2e8f0',
                    background: active ? '#eff6ff' : '#ffffff',
                    color: active ? '#1e40af' : '#475569',
                    fontWeight: active ? 700 : 500, fontSize: '0.82rem',
                    cursor: 'pointer', transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <School size={16} color={active ? '#2563eb' : '#64748b'} />
                  <span>{escItem.escola.nome}</span>
                  <span style={{
                    fontSize: '0.7rem', background: active ? '#dbeafe' : '#f1f5f9',
                    color: active ? '#1e40af' : '#64748b', padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                  }}>
                    {escItem.filhos?.length || 0} dependente(s)
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Carregando dados da instituição escolar...</div>
        ) : !selectedEscola ? (
          <div style={{ textAlign: 'center', padding: 60, background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0' }}>
            <School size={48} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '1.1rem', fontWeight: 700 }}>Nenhuma escola vinculada</h3>
            <p style={{ color: '#64748b', fontSize: '0.84rem', margin: 0 }}>Nenhum dependente com matrícula ativa foi localizado para esta conta.</p>
          </div>
        ) : (
          /* ═══ 2-COLUMN BALANCED DESKTOP SPLIT LAYOUT ═══ */
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 0.75fr)',
            gap: 20,
            alignItems: 'start',
            width: '100%',
          }}>
            {/* ─── Coluna Esquerda: Instituição, Gestão e Professores por Aluno ─── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              
              {/* Card Institucional da Escola */}
              <div style={{
                background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 22,
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 16,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 14, background: '#eff6ff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', flexShrink: 0
                    }}>
                      <Building size={24} />
                    </div>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
                        {selectedEscola.escola.nome}
                      </h2>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: '0.76rem', color: '#16a34a', fontWeight: 600 }}>
                        <CheckCircle2 size={14} /> Unidade Oficial Aluno Conectado
                      </div>
                    </div>
                  </div>

                  {/* Badges de Dependentes nesta Escola */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Dependentes:</span>
                    {(selectedEscola.filhos || []).map((filho) => (
                      <span key={filho.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f8fafc',
                        border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: 10, fontSize: '0.74rem', fontWeight: 700, color: '#334155'
                      }}>
                        <UserAvatar src={filho.fotoUrl} name={filho.nome} role="ALUNO" size="xs" />
                        {filho.nome}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Canais de Contato Reais da Escola */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                  {/* Telefone */}
                  <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: 12, border: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Phone size={12} color="#2563eb" /> Telefone da Secretaria
                    </div>
                    <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                      {selectedEscola.escola.telefone ? (
                        <a href={`tel:${selectedEscola.escola.telefone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                          {selectedEscola.escola.telefone}
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontWeight: 500 }}>Não informado</span>
                      )}
                    </div>
                  </div>

                  {/* WhatsApp */}
                  {(() => {
                    const waNum = getWhatsAppClean(selectedEscola.escola.telefone);
                    return (
                      <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: 12, border: '1px solid #f1f5f9' }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <MessageSquare size={12} color="#16a34a" /> WhatsApp Institucional
                        </div>
                        <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                          {waNum ? (
                            <a
                              href={`https://wa.me/${waNum}?text=${encodeURIComponent('Olá, gostaria de falar com a secretaria escolar.')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#16a34a', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                              {selectedEscola.escola.telefone} <ExternalLink size={12} />
                            </a>
                          ) : (
                            <span style={{ color: '#94a3b8', fontWeight: 500 }}>Não cadastrado</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* E-mail */}
                  <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: 12, border: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Mail size={12} color="#7c3aed" /> E-mail Oficial
                    </div>
                    <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedEscola.escola.email ? (
                        <a href={`mailto:${selectedEscola.escola.email}`} style={{ color: '#7c3aed', textDecoration: 'none' }}>
                          {selectedEscola.escola.email}
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontWeight: 500 }}>Não informado</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ═══ Seção 1: Direção e Equipe Gestora da Escola ═══ */}
              <div style={{
                background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 22,
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 16,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800, color: '#0f172a' }}>
                      Direção & Equipe Gestora
                    </h3>
                    <p style={{ margin: '2px 0 0', fontSize: '0.74rem', color: '#64748b' }}>
                      Canais diretos para agendamentos, secretaria e coordenação pedagógica
                    </p>
                  </div>
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 700, color: '#2563eb', background: '#eff6ff',
                    padding: '4px 10px', borderRadius: 8, border: '1px solid #bfdbfe',
                  }}>
                    Atendimento Escolar
                  </span>
                </div>

                {(!selectedEscola.direcao || selectedEscola.direcao.length === 0) ? (
                  <div style={{ padding: 18, background: '#f8fafc', borderRadius: 12, textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
                    Informações da equipe gestora em processo de atualização pela secretaria.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                    {selectedEscola.direcao.map((membro) => (
                      <div
                        key={membro.id}
                        style={{
                          background: '#f8fafc', borderRadius: 14, border: '1px solid #e2e8f0', padding: 14,
                          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 12,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <UserAvatar src={membro.fotoUrl} name={membro.nome} role="DIRETOR" size="md" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {membro.nome}
                            </div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#2563eb', marginTop: 1 }}>
                              {formatCargo(membro.cargo)}
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleOpenChat(membro)}
                          style={{
                            background: '#ffffff', color: '#2563eb', border: '1px solid #bfdbfe',
                            borderRadius: 8, padding: '7px 12px', fontSize: '0.75rem', fontWeight: 700,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            boxShadow: '0 1px 2px rgba(37,99,235,0.05)', transition: 'all 0.15s',
                          }}
                        >
                          <MessageSquare size={14} /> Conversar pelo Chat
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ═══ Seção 2: Professores Separados por Aluno ═══ */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>
                    Corpo Docente por Aluno
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '0.76rem', color: '#64748b' }}>
                    Professores regentes atribuídos às turmas de cada um dos seus dependentes
                  </p>
                </div>

                {(selectedEscola.filhos || []).map((filho) => (
                  <div
                    key={filho.id}
                    style={{
                      background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 20,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 14,
                    }}
                  >
                    {/* Header do Aluno */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <UserAvatar src={filho.fotoUrl} name={filho.nome} role="ALUNO" size="sm" />
                        <div>
                          <div style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a' }}>
                            {filho.nome}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                            Turma: <strong>{filho.turma || 'Não atribuída'}</strong>
                          </div>
                        </div>
                      </div>

                      <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', background: '#f1f5f9', padding: '4px 10px', borderRadius: 8 }}>
                        {filho.professores?.length || 0} Professor(es)
                      </span>
                    </div>

                    {/* Lista de Professores deste Filho */}
                    {(!filho.professores || filho.professores.length === 0) ? (
                      <div style={{ padding: 18, background: '#f8fafc', borderRadius: 12, textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
                        Professores em processo de alocação de grade para a turma deste aluno.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                        {filho.professores.map((prof) => (
                          <div
                            key={prof.id}
                            style={{
                              background: '#f8fafc', borderRadius: 14, border: '1px solid #e2e8f0', padding: 14,
                              display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 12,
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <UserAvatar src={prof.fotoUrl} name={prof.nome} role="PROFESSOR" size="md" />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {prof.nome}
                                </div>
                                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#059669', marginTop: 1 }}>
                                  {prof.disciplina || 'Professor Regente'}
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() => handleOpenChat(prof, filho.nome)}
                              style={{
                                background: '#ffffff', color: '#059669', border: '1px solid #a7f3d0',
                                borderRadius: 8, padding: '7px 12px', fontSize: '0.75rem', fontWeight: 700,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                boxShadow: '0 1px 2px rgba(5,150,105,0.05)', transition: 'all 0.15s',
                              }}
                            >
                              <MessageSquare size={14} /> Conversar pelo Chat
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>

            {/* ─── Coluna Direita: Localização Real & Segurança ─── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Card de Localização Real */}
              <div style={{
                background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 22,
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontWeight: 800, fontSize: '0.94rem' }}>
                  <MapPin size={18} color="#2563eb" /> Localização & Endereço Oficial
                </div>

                <div style={{ background: '#f8fafc', padding: 14, borderRadius: 12, border: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                    Endereço da Unidade
                  </div>
                  <div style={{ fontSize: '0.86rem', color: '#0f172a', fontWeight: 600, lineHeight: 1.45 }}>
                    {selectedEscola.escola.endereco ? (
                      <>
                        {selectedEscola.escola.endereco}
                        {selectedEscola.escola.cidade && <>, {selectedEscola.escola.cidade}</>}
                        {selectedEscola.escola.uf && <> - {selectedEscola.escola.uf}</>}
                        {selectedEscola.escola.cep && <><br /><span style={{ fontSize: '0.74rem', color: '#64748b' }}>CEP: {selectedEscola.escola.cep}</span></>}
                      </>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>Endereço não cadastrado no sistema.</span>
                    )}
                  </div>
                </div>

                {/* Botões de Ação para Navegação */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <a
                    href={getMapsUrl(selectedEscola.escola)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1, background: '#2563eb', color: '#ffffff', borderRadius: 10,
                      padding: '10px 14px', fontSize: '0.78rem', fontWeight: 700, textDecoration: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      boxShadow: '0 2px 6px rgba(37,99,235,0.2)',
                    }}
                  >
                    <Navigation size={15} /> Abrir Google Maps
                  </a>
                  <a
                    href={getWazeUrl(selectedEscola.escola)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1, background: '#ffffff', color: '#0f172a', borderRadius: 10,
                      border: '1px solid #cbd5e1', padding: '10px 14px', fontSize: '0.78rem',
                      fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: 6,
                    }}
                  >
                    <Compass size={15} /> Traçar no Waze
                  </a>
                </div>
              </div>

              {/* Card de Informações de Apoio */}
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: 20, border: '1px solid #e2e8f0', padding: 20,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontWeight: 800, fontSize: '0.86rem' }}>
                  <ShieldCheck size={16} color="#16a34a" /> Segurança & Apoio Escolar
                </div>
                <div style={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div>
                    🏛️ <strong>Acesso e Atendimento:</strong> Todas as solicitações são registradas e acompanhadas pela coordenação pedagógica.
                  </div>
                  <div>
                    💬 <strong>Comunicação Transparente:</strong> Utilize o chat integrado para mensagens diretas com os professores dos seus dependentes.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ═══ MOBILE VIEW ═══
  return (
    <div className="resp-mobile-root" style={{ background: '#0b1526' }}>
      {/* ═══ NAVY HEADER ═══ */}
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 0 24px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', marginBottom: 16 }}>
          <button 
            onClick={() => navigate('/responsavel/dashboard')} 
            style={{ 
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', 
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', 
              color: 'white', cursor: 'pointer' 
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'white' }}>
              Minha Escola & Contatos
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>
              Equipe diretiva e professores dos filhos
            </p>
          </div>
        </div>
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="resp-content-area" style={{ paddingBottom: 90, marginTop: -16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Carregando dados da escola...</div>
        ) : !selectedEscola ? (
          <div style={{ textAlign: 'center', padding: 32, background: 'white', borderRadius: 16, border: '1px solid #f1f5f9' }}>
            <School size={36} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '0.95rem', fontWeight: 700 }}>Nenhuma escola vinculada</h3>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Card Escola Mobile */}
            <div style={{ background: 'white', borderRadius: 16, padding: 18, border: '1px solid #f1f5f9', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Building size={22} color="#2563eb" />
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>{selectedEscola.escola.nome}</h3>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{selectedEscola.escola.endereco || 'Endereço não informado'}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                {selectedEscola.escola.telefone && (
                  <a
                    href={`tel:${selectedEscola.escola.telefone}`}
                    style={{ flex: 1, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '8px 12px', borderRadius: 8, fontSize: '0.74rem', fontWeight: 700, color: '#2563eb', textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                  >
                    <Phone size={13} /> Ligar
                  </a>
                )}
                <a
                  href={getMapsUrl(selectedEscola.escola)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ flex: 1, background: '#2563eb', padding: '8px 12px', borderRadius: 8, fontSize: '0.74rem', fontWeight: 700, color: '#ffffff', textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                >
                  <Navigation size={13} /> Mapa
                </a>
              </div>
            </div>

            {/* Professores por Aluno Mobile */}
            {(selectedEscola.filhos || []).map((filho) => (
              <div key={filho.id} style={{ background: 'white', borderRadius: 16, padding: 16, border: '1px solid #f1f5f9', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
                <div style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>
                  Professores de {filho.nome}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 12 }}>
                  Turma: {filho.turma || 'Não informada'}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(filho.professores && filho.professores.length > 0) ? (
                    filho.professores.map((prof) => (
                      <div key={prof.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '10px 12px', borderRadius: 10, border: '1px solid #f1f5f9' }}>
                        <div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>{prof.nome}</div>
                          <div style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 600 }}>{prof.disciplina || 'Professor Regente'}</div>
                        </div>
                        <button
                          onClick={() => handleOpenChat(prof, filho.nome)}
                          style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <MessageSquare size={12} /> Chat
                        </button>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '12px', background: '#f8fafc', borderRadius: 10, border: '1px dashed #cbd5e1', textAlign: 'center', color: '#64748b', fontSize: '0.74rem' }}>
                      Professores serão listados após a enturmação e atribuição de aulas.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ BARRA INFERIOR DE NAVEGAÇÃO MOBILE ═══ */}
      <ResponsavelTabBar activeTab="escola" />
    </div>
  );
}
